import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react";
import { NOTIF_POOLS, INBOX_POOLS, PHOTO_BREAK_IMAGES } from "@/i18n/chaos-pools";
import { contentAU } from "@/i18n/content-au";
import { contentUAEar } from "@/i18n/content-uae-ar";
import { contentUAEen } from "@/i18n/content-uae-en";
import { HIW_PLANES, HIW_NOTIFS } from "@/i18n/hiw-data";
import type { RegionKey, LangKey, ContentMap, NotifItem, InboxItem, PlaneSpec, HiwNotifSpec, PhotoBreakImages } from "@/i18n/types";

interface RegionContextValue {
  region: RegionKey;
  lang: LangKey;
  isRTL: boolean;
  setRegion: (region: RegionKey) => void;
  setLang: (lang: LangKey) => void;
  t: (key: string) => string;
  chaosPool: NotifItem[];
  inboxPool: InboxItem[];
  hiwPlanes: PlaneSpec[];
  hiwNotifs: HiwNotifSpec[];
  photoBreakImages: PhotoBreakImages;
}

const RegionContext = createContext<RegionContextValue | null>(null);

function getContentMap(region: RegionKey, lang: LangKey): ContentMap {
  if (region === "UAE" && lang === "ar") return contentUAEar;
  if (region === "UAE") return contentUAEen;
  return contentAU;
}

function getPoolKey(region: RegionKey, lang: LangKey): string {
  if (region === "UAE" && lang === "ar") return "UAE_AR";
  return region;
}

export function RegionProvider({ children }: { children: ReactNode }) {
  const [region, setRegionState] = useState<RegionKey>("AU");
  const [lang, setLangState] = useState<LangKey>("en");

  const isRTL = lang === "ar";

  const setRegion = useCallback((newRegion: RegionKey) => {
    setRegionState(newRegion);
    if (newRegion !== "UAE") {
      setLangState("en");
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
    }
  }, []);

  const setLang = useCallback((newLang: LangKey) => {
    setLangState(newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = newLang;
  }, []);

  const contentMap = useMemo(() => getContentMap(region, lang), [region, lang]);

  const t = useCallback(
    (key: string): string => contentMap[key] ?? contentAU[key] ?? key,
    [contentMap],
  );

  const poolKey = getPoolKey(region, lang);
  const chaosPool = NOTIF_POOLS[poolKey] ?? NOTIF_POOLS.AU;
  const inboxPool = INBOX_POOLS[poolKey] ?? INBOX_POOLS.AU;
  const hiwPlanes = HIW_PLANES[poolKey] ?? HIW_PLANES.AU;
  const hiwNotifs = HIW_NOTIFS[poolKey] ?? HIW_NOTIFS.AU;
  const photoBreakImages = PHOTO_BREAK_IMAGES[region] ?? PHOTO_BREAK_IMAGES.AU;

  const value = useMemo(
    () => ({ region, lang, isRTL, setRegion, setLang, t, chaosPool, inboxPool, hiwPlanes, hiwNotifs, photoBreakImages }),
    [region, lang, isRTL, setRegion, setLang, t, chaosPool, inboxPool, hiwPlanes, hiwNotifs, photoBreakImages],
  );

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
}

export function useRegion(): RegionContextValue {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error("useRegion must be used within a RegionProvider");
  return ctx;
}
